import { createPreview, PreviewConfig } from 'wix-rich-content-preview';
import { type StateBuilder } from 'ricos-content/dist/src/preview/ContentStateBuilder/ContentStateBuilder';
import { ContentStateTransformation, INTERACTIONS } from 'ricos-content/libs/preview';
import React from 'react';
import { ReadMore } from './read-more';
import { SeeFullPost } from './see-full-post';

export const createPostContentPreview = (containerWidth: number, onShowMore: () => void) => {
  const imageCountToShow = containerWidth > 600 ? 3 : 2;
  const galleryStyle = {
    galleryLayout: 2,
    galleryMargin: 0,
    oneRow: false,
    cubeRatio: 1,
    galleryThumbnailsAlignment: 'center',
    isVertical: false,
    imageMargin: 20,
    thumbnailSpacings: 0,
    cubeType: 'fill',
    enableInfiniteScroll: true,
    titlePlacement: 'SHOW_ON_HOVER',
    allowHover: false,
    itemClick: 'link',
    fullscreen: false,
    showArrows: false,
    gridStyle: 1,
    loveButton: false,
    allowSocial: false,
    allowDownload: false,
    mobileSwipeAnimation: 'NO_EFFECT',
    gotStyleParams: true,
    showVideoPlayButton: false,
    videoPlay: 'auto',
    videoSound: false,
    numberOfImagesPerRow: 2,
  };

  const showReadMore = ({ media: { singleMediaItems } }: { media: { singleMediaItems: any[] } }) =>
    singleMediaItems.length < 2;

  const showFullPost = ({
    media: { singleMediaItems },
    textFragments,
    nonMediaPluginsCount,
    collapsablePluginsCount,
  }: {
    media: { singleMediaItems: any[] };
    textFragments: any[];
    nonMediaPluginsCount: number;
    collapsablePluginsCount: number;
  }) =>
    (textFragments.length > 1 && singleMediaItems.length === 0) ||
    (nonMediaPluginsCount > 0 && collapsablePluginsCount > 0);

  const previewTransformation = new ContentStateTransformation({
    _if: (metadata) => metadata.allText.length > 0,
    _then: (metadata, preview) => {
      const {
        textFragments,
        nonMediaPluginsCount,
        collapsablePluginsCount,
        media: { singleMediaItems, galleryItems },
      } = metadata;
      const showToggle =
        showReadMore(metadata) &&
        !showFullPost(metadata) &&
        collapsablePluginsCount === 0 &&
        textFragments.length === 1;
      const previewToDisplay = preview.plain(textFragments[0]).readMore({ lines: 3, showToggle });
      if (
        (showReadMore(metadata) &&
          showFullPost(metadata) &&
          !(singleMediaItems.length > 0 && nonMediaPluginsCount > 0)) ||
        (singleMediaItems.length === 0 && galleryItems.length > 0)
      ) {
        return previewToDisplay.seeFullPost();
      }
      return previewToDisplay;
    },
  })
    .rule({
      _if: (metadata) => metadata.media.singleMediaItems.length === 1,
      _then: (metadata, preview) => {
        const {
          media: { galleryItems, singleMediaItems },
        } = metadata;
        const mediaInfo = singleMediaItems[0];
        const previewToDisplay = preview[
          mediaInfo.type as keyof Omit<StateBuilder, 'contentState' | 'get'>
        ]({
          mediaInfo,
        } as any);
        if (
          showFullPost(metadata) ||
          metadata.textFragments.length > 1 ||
          galleryItems.length > 0
        ) {
          return previewToDisplay.seeFullPost();
        }
        return previewToDisplay;
      },
    })
    .rule({
      _if: (metadata) => metadata.media.singleMediaItems.length > 1,
      _then: ({ media: { galleryItems, singleMediaItems, totalCount } }, preview) => {
        const numberOfImagesPerRow = imageCountToShow;
        const imagesToDisplay = singleMediaItems.slice(0, numberOfImagesPerRow);
        const gallery = preview
          .gallery({
            mediaInfo: imagesToDisplay,
            config: {
              size: 'medium',
            },
            overrides: {
              styles: { ...galleryStyle, numberOfImagesPerRow },
            },
          })
          .seeFullPost();
        if (totalCount - imagesToDisplay.length > 0) {
          return gallery.imageCounter({
            counter: totalCount - imagesToDisplay.length,
          });
        }
        return gallery;
      },
    });

  const defaultPreview = createPreview();
  const contentInteractionMappers = defaultPreview.contentInteractionMappers || [];

  return createPreview({
    onPreviewExpand: onShowMore,
    transformation: previewTransformation,
    contentInteractionMappers: [
      (onPreviewExpand: PreviewConfig['onPreviewExpand']) => {
        return {
          [INTERACTIONS.IMAGE_COUNTER]: (contentInteractionMappers[0]?.(onPreviewExpand) as any)[
            INTERACTIONS.IMAGE_COUNTER
          ],
          [INTERACTIONS.SEE_FULL_CONTENT]: ({ children, ...props }: any) => (
            <SeeFullPost {...{ onPreviewExpand, ...props }}>{children}</SeeFullPost>
          ),
          [INTERACTIONS.READ_MORE]: ({ children, ...props }: any) => (
            <ReadMore {...{ onPreviewExpand, ...props }}>{children}</ReadMore>
          ),
        };
      },
    ],
  });
};
